/**
 * Pagination utility functions
 */

/**
 * Default pagination state
 * @returns {Object} Default pagination state
 */
export const getDefaultPaginationState = () => ({
  total: 0,
  totalPages: 0,
  currentPage: 1,
  pageSize: 10,
  hasPreviousPage: false,
  hasNextPage: false
});

/**
 * Parse pagination data from API response
 * @param {Object} response - The API response with pagination data
 * @returns {Object} Formatted pagination state
 */
export const parsePaginationData = (response) => {
  if (!response) return getDefaultPaginationState();

  return {
    total: response.total || 0,
    totalPages: response.totalPages || 0,
    currentPage: response.currentPage || 1,
    pageSize: response.pageSize || 10,
    hasPreviousPage: response.hasPreviousPage || false,
    hasNextPage: response.hasNextPage || false
  };
};

/**
 * Get query parameters for pagination request
 * @param {number} page - Current page
 * @param {number} limit - Page size
 * @param {string} search - Search term (optional)
 * @param {Object} additionalParams - Additional parameters (optional)
 * @returns {Object} Parameters for API request
 */
export const getPaginationParams = (page = 1, limit = 10, search = '', additionalParams = {}) => {
  const params = {
    page,
    limit,
    ...additionalParams
  };

  if (search && search.trim() !== '') {
    params.search = search;
  }

  return params;
};

/**
 * Calculate page numbers to display
 * @param {number} currentPage - Current page
 * @param {number} totalPages - Total pages
 * @param {number} maxPages - Maximum pages to show (default: 5)
 * @returns {Array} Array of page numbers to display
 */
export const getPageNumbers = (currentPage, totalPages, maxPages = 5) => {
  if (totalPages <= maxPages) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  if (currentPage <= Math.ceil(maxPages / 2)) {
    return Array.from({ length: maxPages }, (_, i) => i + 1);
  } else if (currentPage >= totalPages - Math.floor(maxPages / 2)) {
    return Array.from({ length: maxPages }, (_, i) => totalPages - maxPages + i + 1);
  } else {
    const offset = Math.floor(maxPages / 2);
    return Array.from({ length: maxPages }, (_, i) => currentPage - offset + i);
  }
}; 